import { useCallback } from "react"
import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  ShipFromPreferenceItemInput,
  ShipFromPreferenceUpsertInput,
  ShipFromPreferenceUpsertMutation,
} from "@/graphql/codegen/graphql"
import { ShipFromPreferenceUpsertDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SaveShipFromTransitTimeInput {
  locationId: string
  rows: ShipFromPreferenceItemInput[]
}

export interface UseSaveShipFromTransitTimeProps {
  errorMessage?: string
  successMessage?: string
  onSuccess?: () => void
}

interface UseSaveShipFromTransitTimeResult {
  saveShipFromTransitTime: (input: SaveShipFromTransitTimeInput) => Promise<void>
  isSavingTransitTime: boolean
  error?: CombinedError
}

export const useSaveShipFromTransitTime = ({
  errorMessage = "Failed to save ship from preferences",
  successMessage = "Ship from preferences saved successfully",
  onSuccess,
}: UseSaveShipFromTransitTimeProps = {}): UseSaveShipFromTransitTimeResult => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching, error }, executeMutation] = useMutation<
    ShipFromPreferenceUpsertMutation,
    { input: ShipFromPreferenceUpsertInput }
  >(ShipFromPreferenceUpsertDocument)

  const saveShipFromTransitTime = useCallback(
    async ({ locationId, rows }: SaveShipFromTransitTimeInput): Promise<void> => {
      const input: ShipFromPreferenceUpsertInput = {
        shipToLocationId: locationId,
        preferences: rows.map((row) => ({
          shipFromWarehouseId: row.shipFromWarehouseId,
          estimatedTransitDays: row.estimatedTransitDays ?? null,
          isPreferred: row.isPreferred,
        })),
      }

      const { data, error } = await executeMutation({ input })
      const output = data?.shipFromPreference.upsert

      if (error || output?.__typename === "ShipFromPreferenceUpsertFailure") {
        const failureMessage =
          output?.__typename === "ShipFromPreferenceUpsertFailure"
            ? output.error.message
            : error?.message || errorMessage

        notify({
          type: NotificationType.Error,
          silent: false,
          message: failureMessage,
        })
        return
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        content: successMessage,
      })
      onSuccess?.()
    },
    [executeMutation, notify, errorMessage, successMessage, onSuccess]
  )

  return { saveShipFromTransitTime, isSavingTransitTime: fetching, error }
}
